import Bugsnag from '@bugsnag/js';
import axios from 'axios';
import Core from './core/Core';
import 'classlist-polyfill';
import { store } from './core/store';
import { setRequestTimeout } from './features/ui';
import { ConcurrencyManager } from 'axios-concurrency';

declare let __BUGSNAG_API_KEY__: string;
declare let __APP_ENV__: string;

const windowCoreKeyName = 'PlanneCore';
const legacyCoreKeyKeyName = 'planneCoreKey';
const globalFunctionName = 'plnn';

const makeExtraBreadcrumbs = (code: number) => {
  const urls = ['https://buyer-api.planne.com.br/health', 'https://api.ipify.org/?format=json'];

  for (const u of urls) {
    ((url: string) => {
      Bugsnag.leaveBreadcrumb('Request started', {
        url,
        code,
      });

      fetch(url)
        .then((resp) => {
          Bugsnag.leaveBreadcrumb('Request success', {
            url,
            statusCode: resp.status,
            code,
          });
        })
        .catch((err) => {
          Bugsnag.leaveBreadcrumb('Request failed', {
            url,
            err,
            code,
          });
        });
    })(u);
  }
};

((wdw: Window, doc: Document) => {
  const wdwMap: any = wdw;
  if (wdwMap[windowCoreKeyName]) {
    return;
  }

  const bugsnagKey = __BUGSNAG_API_KEY__;
  const releaseStage = __APP_ENV__;
  if (bugsnagKey) {
    Bugsnag.start({
      apiKey: bugsnagKey,
      releaseStage,
      autoDetectErrors: false,
      onError: (ev) => {
        if (wdwMap[windowCoreKeyName]) {
          ev.addMetadata('core_data', wdwMap[windowCoreKeyName].getData());
        }
      },
    });

    ConcurrencyManager(axios, 5);

    axios.interceptors.request.use(
      (config) => {
        // Tmp code to debug
        if (config.url?.match('buyer-api')) {
          const code = Math.random() * 10000;
          config.headers['X-Req-Id'] = code;
        }

        Bugsnag.leaveBreadcrumb('Preparing request', {
          config,
          navigatorOnLine: navigator.onLine,
          navigatorConn: (navigator as any)['connection'],
        });

        return config;
      },
      (err) => {
        Bugsnag.leaveBreadcrumb('request error', {
          err,
        });

        throw err;
      },
    );

    axios.interceptors.response.use(
      (resp) => resp,
      (err) => {
        if (err.code === 'ECONNABORTED') {
          Bugsnag.notify(err, (event) => {
            event.addMetadata('response', {
              config: err.config,
              data: err.response?.data,
              navigatorOnLine: navigator.onLine,
            });
          });
        }

        throw err;
      },
    );
  }

  axios.interceptors.response.use(
    (config) => config,
    (error) => {
      const urlsRegexToTimeout = [
        /\/products\/[^/]+\/schedulings(\?.*)?$/, // Matches /products/{any}/schedulings
        /\/products\/[^/]+(?:\?.*)?$/, // Matches /products/{any}
        /\/carts\/[^/?]+(?:\?.*)?$/, // Matches /carts/{any}
        /\/carts\/[^/]+\/items(\?.*)?$/, // Matches /carts/{any}/items
        /\/carts\/[^/]+\/checkout(\?.*)?$/, // Matches /carts/{any}/checkout
        /\/carts\/[^/]+\/coupon(\?.*)?$/, // Matches /carts/{any}/coupon
        /\/carts\/[^/]+\/items\/[^/?]+(?:\?.*)?$/, // Matches /carts/{any}/items/{any}
        /\/buyers\/login(\?.*)?$/, // Matches /buyers/login
        /\/buyers\/signup(\?.*)?$/, // Matches /buyers/signup
        /\/orders\/[^/]+\/transact(\?.*)?$/, // Matches /orders/{any}/transact
        /\/orders\/[^/]+\/paymentOptions(\?.*)?$/, // Matches /orders/{any}/paymentOptions
        /\/orders\/[^/]+(?:\?.*)?$/, // Matches /orders/{any}
        /\/orders(?:\?.*)?$/, // Matches /orders
      ];

      const methods = ['post', 'put', 'patch', 'delete'];

      if (
        error.code === 'ECONNABORTED' &&
        urlsRegexToTimeout.some((regex) => regex.test(error.config.url ?? '')) &&
        methods.includes(error.config.method?.toLowerCase() ?? '')
      ) {
        store.dispatch(setRequestTimeout(true));
      }

      return Promise.reject(error);
    },
  );

  const core = new Core(wdw, doc);
  wdwMap[windowCoreKeyName] = core;

  // Code to handle new incorporated script code
  const fun = wdwMap[globalFunctionName];
  if (fun) {
    fun.callMethod = (...args: any[]) => core.callMethod(...args);

    const queue: Array<any[]> = fun.queue ?? [];
    for (const args of queue) {
      core.callMethod(...args);
    }
  }

  // This is a legacy code, necessary for old incorporated script code
  const key = wdwMap[legacyCoreKeyKeyName];
  if (key) {
    core.init(key);
  }
})(window, document);
